import { DateTime, Duration } from 'luxon';
import { DailyHours } from '../clients/quipli-service/location/location';

export function parseIsoTime(time: string) {
  const { hour, minute, second, millisecond } = DateTime.fromISO(time);
  return { hour, minute, second, millisecond };
}

export function findPreviousCloseTime(
  dailyHours: DailyHours[],
  disabledDates: DateTime[],
  startOrEnd: 'rentalStart' | 'rentalEnd',
  dt: DateTime,
) {
  const close = findPreviousOpenTimeBefore(dailyHours, disabledDates, 'rentalEnd', dt);
  const hours = dailyHours.find((h) => h.numericDayOfWeek === close.weekday);
  if (hours && close.diff(close.set(parseIsoTime(hours.rentalEnd.end))).milliseconds !== 0) {
    return findPreviousOpenTimeBefore(dailyHours, disabledDates, startOrEnd, close.minus({ days: 1 }).endOf('day'));
  } else {
    return close;
  }
}

export function findPreviousOpenTimeBefore(
  dailyHours: DailyHours[],
  disabledDates: DateTime[],
  startOrEnd: 'rentalStart' | 'rentalEnd',
  dt: DateTime,
  restrictedTimes: string[] = [],
) {
  if (dailyHours.length === 0) {
    return dt;
  }

  function previousRestrictedTime(dt: DateTime): DateTime {
    if (restrictedTimes.length) {
      const t = [...restrictedTimes].reverse().find((t) => dt.set(parseIsoTime(t)).diff(dt).milliseconds <= 0);
      if (t) {
        return dt.set(parseIsoTime(t));
      } else {
        return dt.minus({ days: 1 }).set(parseIsoTime(restrictedTimes[restrictedTimes.length - 1]));
      }
    } else {
      return dt;
    }
  }

  let previousOpenTime = previousRestrictedTime(dt);
  let daysHours = undefined;

  for (;;) {
    daysHours = dailyHours.find((ho) => ho.numericDayOfWeek === previousOpenTime.weekday);
    const isDisabledDay = disabledDates.some(
      (dd) => dd.startOf('day').diff(previousOpenTime.startOf('day')).milliseconds === 0,
    );
    if (daysHours && !isDisabledDay) {
      const open = previousOpenTime.set(parseIsoTime(daysHours[startOrEnd].start));
      const close = previousRestrictedTime(previousOpenTime.set(parseIsoTime(daysHours[startOrEnd].end)));
      if (previousOpenTime.diff(close).milliseconds > 0) {
        previousOpenTime = close;
      } else if (previousOpenTime.diff(open).milliseconds < 0) {
        previousOpenTime = previousRestrictedTime(previousOpenTime.minus({ days: 1 }).endOf('day'));
        daysHours = undefined;
      }

      if (daysHours) {
        break;
      }
    } else {
      previousOpenTime = previousOpenTime.minus({ days: 1 }).endOf('day');
    }
  }

  return previousOpenTime;
}

export function findNextOpenTimeAfter(
  dailyHours: DailyHours[],
  disabledDates: DateTime[],
  startOrEnd: 'rentalStart' | 'rentalEnd',
  dt: DateTime,
  restrictedTimes: string[],
) {
  if (dailyHours.length === 0) {
    return dt;
  }

  function nextRestrictedTime(dt: DateTime): DateTime {
    if (restrictedTimes.length) {
      const t = restrictedTimes.find((t) => dt.set(parseIsoTime(t)).diff(dt).milliseconds >= 0);
      if (t) {
        return dt.set(parseIsoTime(t));
      } else {
        return dt.plus({ days: 1 }).set(parseIsoTime(restrictedTimes[0]));
      }
    } else {
      return dt;
    }
  }

  let nextOpenTime = nextRestrictedTime(dt);
  let daysHours = undefined;

  for (;;) {
    daysHours = dailyHours.find((ho) => ho.numericDayOfWeek === nextOpenTime.weekday);
    const isDisabledDay = disabledDates.some(
      (dd) => dd.startOf('day').diff(nextOpenTime.startOf('day')).milliseconds === 0,
    );
    if (daysHours && !isDisabledDay) {
      const open = nextRestrictedTime(nextOpenTime.set(parseIsoTime(daysHours[startOrEnd].start)));
      const close = nextOpenTime.set(parseIsoTime(daysHours[startOrEnd].end));
      if (nextOpenTime.diff(open).milliseconds < 0) {
        nextOpenTime = open;
      } else if (nextOpenTime.diff(close).milliseconds > 0) {
        nextOpenTime = nextRestrictedTime(nextOpenTime.plus({ days: 1 }).startOf('day'));
        daysHours = undefined;
      }

      if (daysHours) {
        break;
      }
    } else {
      nextOpenTime = nextOpenTime.plus({ days: 1 }).startOf('day');
    }
  }

  return nextOpenTime;
}

export function ceilToStep(dt: DateTime, step: Duration) {
  const remainder = dt.diff(dt.startOf('day'), 'milliseconds').milliseconds % step.as('milliseconds');
  if (remainder > 0) {
    return dt.plus(step.as('milliseconds') - remainder);
  } else {
    return dt;
  }
}

export function floorToStep(dt: DateTime, step: Duration) {
  const remainder = dt.diff(dt.startOf('day'), 'milliseconds').milliseconds % step.as('milliseconds');
  return dt.minus(remainder);
}

export function durationToHuman(d: Duration) {
  return d
    .shiftTo('year', 'month', 'week', 'day', 'hour')
    .toHuman()
    .split(', ')
    .filter((part) => part[0] !== '0')
    .join(', ');
}
